import { FunctionalComponent } from 'preact'
import { Loader2 } from 'lucide-preact'
import { useNonProspectDomains, type NonProspectDomainsScope } from '@/hooks/useNonProspectDomains'
import { NonProspectDomainsPanel } from './NonProspectDomainsPanel'

interface NonProspectDomainsCardProps {
  scope: NonProspectDomainsScope
  readOnly?: boolean
  /** Omit the card chrome when the host screen already provides it. */
  bare?: boolean
}

/**
 * Card shell for the non-prospect domain editor (ORB-619): owns the fetch, renders loading and
 * error states, and hands the loaded view to the presentation-only panel.
 *
 * Unlike ApprovedDomainsCard this one fetches rather than taking lifted state, because its list is
 * its own sub-resource with its own ETag — there is no sibling card on either screen that could
 * clobber it.
 */
export const NonProspectDomainsCard: FunctionalComponent<NonProspectDomainsCardProps> = ({
  scope,
  readOnly = false,
  bare = false,
}) => {
  const { view, loading, error, save } = useNonProspectDomains(scope)

  const body = loading ? (
    <div className="flex items-center gap-2 text-sm text-slate-500 dark:text-slate-400 py-6">
      <Loader2 className="w-4 h-4 animate-spin" /> Loading non-prospect domains…
    </div>
  ) : error || !view ? (
    <div className="text-sm text-red-600 dark:text-red-400 py-6">
      {error ?? 'Unable to load non-prospect domains.'}
    </div>
  ) : (
    <NonProspectDomainsPanel
      view={view}
      onSave={save}
      level={scope.level}
      readOnly={readOnly}
    />
  )

  if (bare) return <>{body}</>

  return (
    <div className="rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 p-6 shadow-sm">
      {body}
    </div>
  )
}
